// src/pages/bookings/AdminBookingDetailsPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import AdminLayout from "@/layouts/AdminLayout";
import { useAdminBookingById, useAdminTravelPlan } from "@/api/features/bookings/queries";

import {
  useAdminUpdateBookingStatusMutation,
  useAdminUpsertTravelPlanMutation,
  useAdminDeleteTravelPlanMutation,
  useAdminGenerateHdfcPaymentLinkMutation,
  useAdminGenerateVoucherMutation, // ✅ NEW
} from "@/api/features/bookings/mutations";

import type {
  AdminTravelPlanResult,
  AdminUpsertTravelPlanBody,
  PaymentProvider,
  PaymentKind,
  TravelMode,
  TravelPlanStatus,
  TravelSegment,
  AdminGeneratePaymentLinkBody,
  AdminGeneratePaymentLinkResult,

  // ✅ NEW voucher
  AdminGenerateVoucherBody,
  AdminGenerateVoucherResult,
} from "@/api/features/bookings/types";

/* ============================================================================
   Helpers
============================================================================ */

type SegmentDraft = {
  key: string;
  mode: TravelMode;
  date: string; // YYYY-MM-DD
  from: string;
  to: string;
  details: string;
  departure: string; // HH:mm or ""
  arrival: string; // HH:mm or ""
};

function computeTpStatus(segs: SegmentDraft[]): TravelPlanStatus {
  return segs.length > 0 ? "SHARED" : "NOT_SHARED";
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatDateShort(iso?: string | null) {
  const s = (iso || "").trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function safeNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(amount: unknown, currencyCode?: string | null) {
  const n = safeNum(amount);
  if (n === null) return "—";
  const code = String(currencyCode || "").trim().toUpperCase() || "INR";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${code} ${Math.round(n * 100) / 100}`;
  }
}

function formatGeneratePaymentLinkError(error: unknown) {
  const message = String((error as any)?.message || "").trim();
  if (!message) return "Failed to generate payment link.";

  const normalized = message.toLowerCase();
  if (
    normalized.includes("expire_by") &&
    normalized.includes("atleast 15 minutes in future")
  ) {
    return "Expiry must be at least 15 minutes from now. Please choose a later date or leave expiry blank.";
  }

  if (
    normalized.includes("hdfc_not_configured") ||
    normalized.includes("gateway_not_configured") ||
    normalized.includes("authentication failed")
  ) {
    return "HDFC SmartGateway is not configured correctly. Please check the merchant credentials.";
  }

  return message;
}

function formatVoucherError(error: unknown) {
  const message = String((error as any)?.message || "").trim();
  if (!message) return "Failed to generate voucher.";

  const normalized = message.toLowerCase();
  if (
    normalized.includes("aws access key id") ||
    normalized.includes("secret access key") ||
    normalized.includes("signature we calculated does not match")
  ) {
    return "Voucher PDF could not be saved. Please check the AWS S3 credentials configured for the API.";
  }

  if (normalized.includes("aws_s3_assets_bucket is not configured")) {
    return "Voucher upload is not configured. Please set the S3 bucket and asset base URL in the API environment.";
  }

  return message;
}

function StatusPill({ status }: { status: string }) {
  const s = String(status || "").toUpperCase();

  const cls =
    s === "ON_HOLD"
      ? "bg-violet-50 text-violet-800 border-violet-200"
      : s === "WAITING_PAYMENT"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : s === "PARTIALLY_PAID"
      ? "bg-yellow-50 text-yellow-800 border-yellow-200"
      : s === "CONFIRMED"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : s === "DRAFT"
      ? "bg-slate-50 text-slate-700 border-slate-200"
      : "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs font-medium", cls)}>
      {s}
    </span>
  );
}

function getTourSnapshot(payload: any) {
  return payload?.booking?.pricingSnapshot?.tourSnapshot ?? null;
}

function getTourTitle(payload: any) {
  const ts = getTourSnapshot(payload);
  return (
    ts?.name ||
    payload?.booking?.pricingSnapshot?.items?.[0]?.title ||
    payload?.items?.[0]?.title ||
    "Tour"
  );
}

function getHeaderImageUrl(payload: any) {
  const ts = getTourSnapshot(payload);
  return ts?.headerImageUrl || ts?.images?.[0]?.imageUrl || null;
}

function getCityLabel(tourSnapshot: any) {
  const cities = Array.isArray(tourSnapshot?.cities) ? tourSnapshot.cities : [];
  if (!cities.length) return null;

  const first = cities[0];
  if (cities.length === 1) {
    return `${first?.city || ""}${first?.country ? `, ${first.country}` : ""}`.trim();
  }
  const firstLabel = `${first?.city || ""}${first?.country ? `, ${first.country}` : ""}`.trim();
  return `${firstLabel} + ${cities.length - 1} more`;
}

function buildPaxLabel(paxJson: any) {
  if (!paxJson) return "—";
  const a = Number(paxJson.adults ?? 0);
  const cwb = Number(paxJson.childWb ?? 0);
  const cwob = Number(paxJson.childWob ?? 0);
  const inf = Number(paxJson.infants ?? 0);

  const parts: string[] = [];
  parts.push(`${a} Adult${a === 1 ? "" : "s"}`);
  if (cwb) parts.push(`${cwb} Child (With bed)`);
  if (cwob) parts.push(`${cwob} Child (No bed)`);
  if (inf) parts.push(`${inf} Infant${inf === 1 ? "" : "s"}`);
  return parts.join(" • ");
}

function normalizeListStrings(input: unknown): string[] {
  const arr = Array.isArray(input) ? input : [];
  return arr
    .map((x: any) => {
      const label =
        x?.label ?? x?.text ?? x?.name ?? x?.title ?? x?.policyText ?? x?.description ?? x;
      return String(label ?? "").trim();
    })
    .filter(Boolean);
}

function getBookingTourItinerary(booking: any, tourSnapshot: any) {
  const fromBooking = Array.isArray(booking?.itineraryJson) ? booking.itineraryJson : null;
  const fromSnapshot = Array.isArray(tourSnapshot?.itineraryDays) ? tourSnapshot.itineraryDays : [];
  const days = Array.isArray(fromBooking) ? fromBooking : fromSnapshot;
  return [...days].sort((a, b) => Number(a?.dayNumber ?? 0) - Number(b?.dayNumber ?? 0));
}

function getBookingTourHotels(booking: any, tourSnapshot: any) {
  const fromBooking = Array.isArray(booking?.hotelDetailsJson) ? booking.hotelDetailsJson : null;
  const fromSnapshot = Array.isArray(tourSnapshot?.hotels) ? tourSnapshot.hotels : [];
  const rows = Array.isArray(fromBooking) ? fromBooking : fromSnapshot;

  return rows.map((x: any, idx: number) => ({
    key: `${idx}-${x?.hotelName ?? x?.name ?? ""}-${x?.date ?? ""}`,
    hotelName: String(x?.hotelName ?? x?.name ?? "").trim() || "Hotel",
    date: String(x?.date ?? "").trim() || null,
    startTime: String(x?.startTime ?? "").trim() || null,
    endTime: String(x?.endTime ?? "").trim() || null,
    city: String(x?.city ?? "").trim() || null,
    country: String(x?.country ?? "").trim() || null,
    hotelCategory: String(x?.hotelCategory ?? "").trim() || null,
    roomType: String(x?.roomType ?? "").trim() || null,
    mealPlan: String(x?.mealPlan ?? "").trim() || null,
    confirmationNumber: String(x?.confirmationNumber ?? "").trim() || null,
    notes: String(x?.notes ?? "").trim() || null,
  }));
}

function isHttpsUrl(s: string) {
  try {
    const u = new URL(s);
    return u.protocol === "https:";
  } catch {
    return false;
  }
}

function isYmd(s: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function prettyEnumLabel(v: string) {
  return String(v || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

function openExternal(url: string) {
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // ignore
  }
}

function normalizeUrlCandidate(v: unknown): string | null {
  const s = String(v ?? "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return null;
}

function resolveFlightTicketUrl(booking: any, items: any[]): string | null {
  const bookingCandidates = [
    booking?.ticketUrl,
    booking?.ticketPdfUrl,
    booking?.eTicketUrl,
    booking?.pnrPdfUrl,
    booking?.voucher?.url,
    booking?.pricingSnapshot?.ticketUrl,
    booking?.pricingSnapshot?.ticketPdfUrl,
    booking?.pricingSnapshot?.eTicketUrl,
  ];

  for (const c of bookingCandidates) {
    const url = normalizeUrlCandidate(c);
    if (url) return url;
  }

  for (const it of items || []) {
    const itemCandidates = [
      it?.ticketUrl,
      it?.ticketPdfUrl,
      it?.metaJson?.ticketUrl,
      it?.metaJson?.ticketPdfUrl,
      it?.metaJson?.eTicketUrl,
      it?.metaJson?.downloadUrl,
      it?.pricingJson?.ticketUrl,
      it?.pricingJson?.ticketPdfUrl,
    ];
    for (const c of itemCandidates) {
      const url = normalizeUrlCandidate(c);
      if (url) return url;
    }
  }

  return null;
}

function detectFlightBooking(booking: any, items: any[]): boolean {
  const snapshotItems = Array.isArray(booking?.pricingSnapshot?.items)
    ? booking.pricingSnapshot.items
    : [];

  const hasFlightInSnapshot = snapshotItems.some(
    (it: any) => String(it?.productType || "").toUpperCase() === "FLIGHT"
  );

  const hasFlightItem = (items || []).some(
    (it: any) => String(it?.productType || "").toUpperCase() === "FLIGHT"
  );

  return hasFlightInSnapshot || hasFlightItem;
}

type FlightSegmentView = {
  tripIndicator: number | null;
  segmentIndicator: number | null;
  airlineCode: string;
  airline: string;
  flightNumber: string;
  fromCode: string;
  fromCity: string;
  toCode: string;
  toCity: string;
  departureAt: string;
  arrivalAt: string;
  cabin: string;
  duration: string;
};

function flattenAny(input: any): any[] {
  if (!Array.isArray(input)) return [];
  const out: any[] = [];
  for (const v of input) {
    if (Array.isArray(v)) out.push(...flattenAny(v));
    else out.push(v);
  }
  return out;
}

function asText(v: unknown) {
  return String(v ?? "").trim();
}

function pickFlightSegmentsFromNode(node: any): any[] {
  if (!node || typeof node !== "object") return [];
  const candidates = [
    node?.Segments,
    node?.segments,
    node?.FlightItinerary?.Segments,
    node?.flightItinerary?.segments,
    node?.Response?.FlightItinerary?.Segments,
    node?.Response?.Segments,
    node?.response?.Response?.FlightItinerary?.Segments,
    node?.response?.Response?.Segments,
    node?.pricingSnapshot?.response?.Response?.FlightItinerary?.Segments,
    node?.pricingSnapshot?.response?.Response?.Segments,
    node?.pricingSnapshot?.segments,
    node?.pricingSnapshot?.flightSegments,
    node?.metaJson?.Segments,
    node?.metaJson?.segments,
    node?.metaJson?.FlightItinerary?.Segments,
    node?.metaJson?.flightItinerary?.segments,
    node?.pricingJson?.Segments,
    node?.pricingJson?.segments,
    node?.pricingJson?.FlightItinerary?.Segments,
    node?.pricingJson?.flightItinerary?.segments,
  ];

  const all: any[] = [];
  for (const c of candidates) {
    if (Array.isArray(c)) all.push(...flattenAny(c));
  }
  return all;
}

function extractFlightSegments(booking: any, items: any[]): FlightSegmentView[] {
  const raw = [...pickFlightSegmentsFromNode(booking), ...pickFlightSegmentsFromNode(booking?.pricingSnapshot)];
  for (const it of items || []) raw.push(...pickFlightSegmentsFromNode(it));

  const mapped = raw
    .map((seg: any) => {
      const fromCode = asText(
        seg?.Origin?.Airport?.AirportCode ??
          seg?.origin?.airportCode ??
          seg?.originAirportCode ??
          seg?.fromCode ??
          seg?.originCode
      );
      const toCode = asText(
        seg?.Destination?.Airport?.AirportCode ??
          seg?.destination?.airportCode ??
          seg?.destinationAirportCode ??
          seg?.toCode ??
          seg?.destinationCode
      );
      if (!fromCode && !toCode) return null;

      return {
        tripIndicator: Number.isFinite(Number(seg?.TripIndicator ?? seg?.tripIndicator))
          ? Number(seg?.TripIndicator ?? seg?.tripIndicator)
          : null,
        segmentIndicator: Number.isFinite(Number(seg?.SegmentIndicator ?? seg?.segmentIndicator))
          ? Number(seg?.SegmentIndicator ?? seg?.segmentIndicator)
          : null,
        airlineCode: asText(seg?.Airline?.AirlineCode ?? seg?.airlineCode),
        airline: asText(
          seg?.Airline?.AirlineName ?? seg?.airlineName ?? seg?.Airline?.AirlineCode ?? seg?.airlineName ?? seg?.airlineCode
        ),
        flightNumber: asText(seg?.Airline?.FlightNumber ?? seg?.flightNumber),
        fromCode,
        fromCity: asText(seg?.Origin?.Airport?.CityName ?? seg?.origin?.cityName),
        toCode,
        toCity: asText(seg?.Destination?.Airport?.CityName ?? seg?.destination?.cityName),
        departureAt: asText(seg?.Origin?.DepTime ?? seg?.departureTime ?? seg?.depTime ?? seg?.departureAt),
        arrivalAt: asText(seg?.Destination?.ArrTime ?? seg?.arrivalTime ?? seg?.arrTime ?? seg?.arrivalAt),
        cabin: asText(seg?.CabinClass ?? seg?.cabinClass ?? seg?.cabin ?? seg?.fareClass),
        duration: asText(seg?.Duration ?? seg?.duration ?? seg?.durationMinutes),
      } as FlightSegmentView;
    })
    .filter(Boolean) as FlightSegmentView[];

  return mapped.sort((a, b) => {
    const ta = a.tripIndicator ?? 999;
    const tb = b.tripIndicator ?? 999;
    if (ta !== tb) return ta - tb;
    const sa = a.segmentIndicator ?? 999;
    const sb = b.segmentIndicator ?? 999;
    return sa - sb;
  });
}

function formatDateTimeParts(v?: string | null) {
  const s = asText(v);
  if (!s) return { time: "—", date: "—" };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { time: s, date: "—" };
  return {
    time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }),
    date: d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }),
  };
}

function airlineLogoUrl(code?: string | null) {
  const c = asText(code).toUpperCase();
  return `https://cdn.tranzytours.com/assets/airlines/logo/${c || "nologo"}.gif`;
}

function getFlightItineraryNode(payload: any) {
  return (
    payload?.flight?.rawResponse?.Response?.Response?.FlightItinerary ??
    payload?.flight?.rawResponse?.Response?.FlightItinerary ??
    payload?.booking?.pricingSnapshot?.itinerary ??
    null
  );
}

function normalizeSsrList(input: any): any[] {
  if (Array.isArray(input)) return input;
  return input ? [input] : [];
}

function extractPassengerSsr(payload: any) {
  const itinerary = getFlightItineraryNode(payload);
  const paxRows = Array.isArray(itinerary?.Passenger) ? itinerary.Passenger : [];
  return paxRows.map((pax: any) => {
    const name = [asText(pax?.Title), asText(pax?.FirstName), asText(pax?.LastName)]
      .filter(Boolean)
      .join(" ")
      .trim();
    const baggage = normalizeSsrList(pax?.Baggage).map((x: any) => asText(x?.AirlineDescription ?? x?.Description ?? x?.Code)).filter(Boolean);
    const meals = normalizeSsrList(pax?.MealDynamic).map((x: any) => asText(x?.AirlineDescription ?? x?.Description ?? x?.Code)).filter(Boolean);
    const seats = normalizeSsrList(pax?.SeatDynamic).map((x: any) => asText(x?.SeatNo ?? x?.Code ?? x?.Description)).filter(Boolean);
    return { name: name || "Passenger", baggage, meals, seats };
  });
}

function extractFlightGstDetails(payload: any) {
  const itinerary = getFlightItineraryNode(payload);
  const paxRows = Array.isArray(itinerary?.Passenger) ? itinerary.Passenger : [];
  const normalize = (v: unknown) => String(v ?? "").trim();
  const hasAnyGst = (pax: any) =>
    Boolean(
      normalize(pax?.GSTNumber) ||
        normalize(pax?.GSTCompanyName) ||
        normalize(pax?.GSTCompanyEmail) ||
        normalize(pax?.GSTCompanyContactNumber) ||
        normalize(pax?.GSTCompanyAddress)
    );

  const preferred =
    paxRows.find((pax: any) => Boolean(pax?.IsLeadPax) && hasAnyGst(pax)) ??
    paxRows.find((pax: any) => hasAnyGst(pax)) ??
    null;

  const gst = {
    number: normalize(preferred?.GSTNumber),
    companyName: normalize(preferred?.GSTCompanyName),
    companyEmail: normalize(preferred?.GSTCompanyEmail),
    companyContactNumber: normalize(preferred?.GSTCompanyContactNumber),
    companyAddress: normalize(preferred?.GSTCompanyAddress),
  };

  return {
    ...gst,
    hasGst: Boolean(
      gst.number || gst.companyName || gst.companyEmail || gst.companyContactNumber || gst.companyAddress
    ),
  };
}

function escapeHtml(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Render a print-ready e-ticket / itinerary receipt from booking data so the
 * user can print or "Save as PDF" — used when the supplier didn't provide a
 * hosted ticket PDF (otherwise we just open that URL).
 */
function buildFlightTicketHtml(args: {
  bookingCode: string;
  pnr: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  segments: FlightSegmentView[];
  travelers: any[];
}): string {
  const {
    bookingCode,
    pnr,
    customerName,
    customerEmail,
    customerPhone,
    segments,
    travelers,
  } = args;

  const paxRows =
    (travelers || [])
      .map((t, i) => {
        const name =
          `${t?.firstName || ""} ${t?.lastName || ""}`.trim() || "—";
        const type = escapeHtml(t?.paxType || t?.type || "—");
        return `<tr><td>${i + 1}</td><td>${escapeHtml(
          name
        )}</td><td>${type}</td></tr>`;
      })
      .join("") ||
    `<tr><td colspan="3" style="text-align:center;color:#888">No passengers listed</td></tr>`;

  const segRows =
    (segments || [])
      .map((s) => {
        const dep = formatDateTimeParts(s.departureAt);
        const arr = formatDateTimeParts(s.arrivalAt);
        const flight = [s.airlineCode, s.flightNumber].filter(Boolean).join(" ");
        const airline = escapeHtml(s.airline || s.airlineCode || "—");
        const from = escapeHtml(
          [s.fromCity, s.fromCode].filter(Boolean).join(" · ") || "—"
        );
        const to = escapeHtml(
          [s.toCity, s.toCode].filter(Boolean).join(" · ") || "—"
        );
        return `<tr>
          <td><div class="b">${airline}</div><div class="muted">${escapeHtml(
          flight
        )}</div></td>
          <td>${from}</td>
          <td>${to}</td>
          <td><div class="b">${escapeHtml(
            dep.time
          )}</div><div class="muted">${escapeHtml(dep.date)}</div></td>
          <td><div class="b">${escapeHtml(
            arr.time
          )}</div><div class="muted">${escapeHtml(arr.date)}</div></td>
          <td>${escapeHtml(s.cabin || "—")}</td>
        </tr>`;
      })
      .join("") ||
    `<tr><td colspan="6" style="text-align:center;color:#888">No flight segments available</td></tr>`;

  return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>E-Ticket ${escapeHtml(bookingCode)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Arial,Helvetica,sans-serif;color:#111827;margin:0;padding:24px}
  .wrap{max-width:800px;margin:0 auto}
  .head{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #0ea5e9;padding-bottom:12px}
  .head img{height:40px}
  .head .t{text-align:right}
  .head .t h1{font-size:18px;margin:0;color:#0369a1}
  .head .t .muted{font-size:12px;color:#6b7280}
  .meta{display:flex;gap:24px;flex-wrap:wrap;margin:16px 0}
  .meta .cell .lbl{color:#6b7280;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
  .meta .cell .val{font-weight:700;font-size:13px}
  h2{font-size:13px;text-transform:uppercase;letter-spacing:.04em;color:#374151;margin:20px 0 8px}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th{text-align:left;background:#f3f4f6;color:#374151;font-size:11px;text-transform:uppercase;letter-spacing:.03em;padding:8px;border-bottom:1px solid #e5e7eb}
  td{padding:8px;border-bottom:1px solid #f1f5f9;vertical-align:top}
  .b{font-weight:700}
  .muted{color:#6b7280;font-size:11px}
  .foot{margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:11px;color:#6b7280}
  @media print{body{padding:0}}
</style></head>
<body><div class="wrap">
  <div class="head">
    <img src="https://cdn.tranzytours.com/assets/branding/logo.png" alt="Tranzy Tours"/>
    <div class="t"><h1>E-Ticket / Itinerary</h1><div class="muted">Issued by Tranzy Tours</div></div>
  </div>
  <div class="meta">
    <div class="cell"><div class="lbl">Booking Ref</div><div class="val">${escapeHtml(
      bookingCode || "—"
    )}</div></div>
    <div class="cell"><div class="lbl">PNR</div><div class="val">${escapeHtml(
      pnr || "—"
    )}</div></div>
    <div class="cell"><div class="lbl">Customer</div><div class="val">${escapeHtml(
      customerName || "—"
    )}</div></div>
    <div class="cell"><div class="lbl">Contact</div><div class="val">${escapeHtml(
      customerEmail
    )} · ${escapeHtml(customerPhone)}</div></div>
  </div>
  <h2>Passengers</h2>
  <table><thead><tr><th>#</th><th>Name</th><th>Type</th></tr></thead><tbody>${paxRows}</tbody></table>
  <h2>Flights</h2>
  <table><thead><tr><th>Airline / Flight</th><th>From</th><th>To</th><th>Departs</th><th>Arrives</th><th>Cabin</th></tr></thead><tbody>${segRows}</tbody></table>
  <div class="foot">This is a computer-generated itinerary receipt. Please carry a valid photo ID and arrive at the airport at least 2 hours before departure. Baggage and fare rules are per the airline's policy.</div>
</div></body></html>`;
}

function printHtmlDocument(html: string) {
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) {
    window.alert("Please allow pop-ups for this site to print the ticket.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();

  const triggerPrint = () => {
    try {
      w.print();
    } catch {
      /* ignore */
    }
  };

  // Print once the content (incl. the logo) has loaded.
  w.onload = () => setTimeout(triggerPrint, 300);
  setTimeout(triggerPrint, 800); // fallback if onload doesn't fire
}

function normalizeVoucherSnapshot(raw: any): AdminGenerateVoucherResult | null {
  if (!raw || typeof raw !== "object") return null;
  const voucherId = Number(raw?.voucherId ?? raw?.id ?? 0);
  const voucherNumber = String(raw?.voucherNumber ?? raw?.number ?? "").trim();
  const rawUrl = String(raw?.url ?? "").trim();
  const version = Number(raw?.version ?? 0);
  const rawKey = String(raw?.key ?? "").trim();
  const vendorRefNumber = String(raw?.vendorRefNumber ?? "").trim() || null;

  if (!Number.isFinite(voucherId) || voucherId <= 0) return null;
  if (!voucherNumber) return null;

  return {
    ok: true,
    voucherId,
    voucherNumber,
    vendorRefNumber,
    version: Number.isFinite(version) && version > 0 ? version : 1,
    url: rawUrl || null,
    key: rawKey || null,
  };
}

function voucherStorageKey(bookingId: number | string) {
  return `admin:booking:voucher:${String(bookingId)}`;
}

function readStoredVoucher(bookingId: number | string): AdminGenerateVoucherResult | null {
  try {
    const raw = localStorage.getItem(voucherStorageKey(bookingId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeVoucherSnapshot(parsed);
  } catch {
    return null;
  }
}

function writeStoredVoucher(bookingId: number | string, voucher: AdminGenerateVoucherResult) {
  try {
    localStorage.setItem(voucherStorageKey(bookingId), JSON.stringify(voucher));
  } catch {
    // ignore
  }
}

function mailtoHref(opts: { to?: string | null; subject: string; body: string }) {
  const to = (opts.to || "").trim();
  const subject = encodeURIComponent(opts.subject);
  const body = encodeURIComponent(opts.body);
  return `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}`;
}

function whatsappHref(text: string) {
  // Works on desktop + mobile. WhatsApp will handle login/app switch.
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/* ============================================================================
   Modal
============================================================================ */

function Modal({
  open,
  title,
  subtitle,
  children,
  onClose,
  footer,
  widthClass = "max-w-5xl",
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  widthClass?: string;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center p-4 sm:p-6 overflow-auto">
        <div
          className={cn(
            "w-full rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden pointer-events-auto",
            widthClass
          )}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 bg-gray-50">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">{title}</div>
              {subtitle ? <div className="mt-0.5 text-xs text-gray-500">{subtitle}</div> : null}
            </div>

            <button
              onClick={onClose}
              className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
              aria-label="Close modal"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[78vh] overflow-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {footer}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   Travel plan editor helpers
============================================================================ */

const TRAVEL_MODE_OPTIONS: TravelMode[] = ["AIR", "BUS", "RAIL", "ROAD", "WATER"];

function makeKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function segmentFromApi(s: TravelSegment): SegmentDraft {
  return {
    key: makeKey(),
    mode: s.mode,
    date: String(s.date || ""),
    from: String(s.from || ""),
    to: String(s.to || ""),
    details: String(s.details || ""),
    departure: s.departure ? String(s.departure) : "",
    arrival: s.arrival ? String(s.arrival) : "",
  };
}

function segmentToApi(s: SegmentDraft): TravelSegment {
  const dep = s.departure.trim();
  const arr = s.arrival.trim();
  return {
    mode: s.mode,
    date: s.date.trim(),
    from: s.from.trim(),
    to: s.to.trim(),
    details: s.details.trim(),
    departure: dep ? dep : null,
    arrival: arr ? arr : null,
    meta: null,
  };
}

function validateSegments(list: SegmentDraft[]) {
  const errs: string[] = [];
  list.forEach((s, i) => {
    const row = i + 1;
    if (!s.mode) errs.push(`Segment ${row}: mode is required`);
    if (!s.details.trim()) errs.push(`Segment ${row}: details is required`);
    if (!s.date.trim() || !isYmd(s.date.trim())) errs.push(`Segment ${row}: date must be YYYY-MM-DD`);
    if (!s.from.trim()) errs.push(`Segment ${row}: from is required`);
    if (!s.to.trim()) errs.push(`Segment ${row}: to is required`);
    if (s.departure.trim() && !/^\d{2}:\d{2}$/.test(s.departure.trim()))
      errs.push(`Segment ${row}: departure must be HH:mm`);
    if (s.arrival.trim() && !/^\d{2}:\d{2}$/.test(s.arrival.trim()))
      errs.push(`Segment ${row}: arrival must be HH:mm`);
  });
  return errs;
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => pad2(i * 5));

function splitHm(v?: string | null) {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{2}):(\d{2})$/);
  return m ? { hh: m[1], mm: m[2] } : { hh: "", mm: "" };
}

function TimeHmCell(props: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  disabled?: boolean;
}) {
  const { value, onChange, disabled } = props;
  const { hh, mm } = splitHm(value);

  const setParts = (nextH?: string, nextM?: string) => {
    const h = (nextH ?? hh ?? "").trim();
    const m = (nextM ?? mm ?? "").trim();
    if (!h && !m) return onChange(null);
    const h2 = h || "00";
    const m2 = m || "00";
    onChange(`${h2}:${m2}`);
  };

  return (
    <div className="inline-flex items-center gap-1">
      <select
        value={hh}
        disabled={disabled}
        onChange={(e) => setParts(e.target.value, mm)}
        className="h-7 w-[56px] rounded-md border border-gray-200 bg-white px-1.5 text-xs outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
        title="Hour"
      >
        <option value="">HH</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>

      <span className="text-gray-400 text-xs font-mono">:</span>

      <select
        value={mm}
        disabled={disabled}
        onChange={(e) => setParts(hh, e.target.value)}
        className="h-7 w-[56px] rounded-md border border-gray-200 bg-white px-1.5 text-xs outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
        title="Minute"
      >
        <option value="">MM</option>
        {MINUTES_5.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>

      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(null)}
        className={cn(
          "h-7 rounded-md border px-2 text-[11px] leading-none",
          disabled
            ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
            : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
        )}
        title="Clear time"
      >
        Clear
      </button>
    </div>
  );
}

/* ============================================================================
   Page
============================================================================ */

export default function AdminBookingDetailsPage() {
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();
  const isFlightPage = pathname.startsWith("/flight-bookings");
  const listPath = isFlightPage ? "/flight-bookings" : "/bookings";

  const bookingId = id ? String(id) : null;
  const enabled = !!bookingId && bookingId.trim() !== "";

  const detailQ = useAdminBookingById(bookingId);
  const travelPlanQ = useAdminTravelPlan(bookingId);

  const payload: any = detailQ.data ?? null;
  const booking: any = payload?.booking ?? null;

  const customer: any = payload?.customer ?? booking?.customer ?? null;

  const customerName =
    booking?.customerName ||
    customer?.displayName ||
    customer?.companyName ||
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
    booking?.contactName ||
    booking?.contactEmail ||
    (booking?.customerUserId != null ? `User #${booking.customerUserId}` : "—");

  const customerEmail = booking?.customerEmail || customer?.email || booking?.contactEmail || "—";
  const customerPhone = booking?.customerPhone || customer?.phone || booking?.contactPhone || "—";

  const items: any[] = Array.isArray(payload?.items) ? payload.items : [];
  const travelers: any[] = Array.isArray(payload?.travelers) ? payload.travelers : [];

  const tourSnapshot = useMemo(() => getTourSnapshot(payload), [payload]);
  const title = useMemo(() => getTourTitle(payload), [payload]);
  const headerImageUrl = useMemo(() => getHeaderImageUrl(payload), [payload]);
  const cityLabel = useMemo(() => getCityLabel(tourSnapshot), [tourSnapshot]);
  const itineraryDays = useMemo(() => getBookingTourItinerary(booking, tourSnapshot), [booking, tourSnapshot]);
  const hotelRows = useMemo(() => getBookingTourHotels(booking, tourSnapshot), [booking, tourSnapshot]);
  const inclusionRows = useMemo(
    () =>
      normalizeListStrings(
        Array.isArray(booking?.inclusionsJson) ? booking.inclusionsJson : tourSnapshot?.inclusions
      ),
    [booking, tourSnapshot]
  );
  const exclusionRows = useMemo(
    () =>
      normalizeListStrings(
        Array.isArray(booking?.exclusionsJson) ? booking.exclusionsJson : tourSnapshot?.exclusions
      ),
    [booking, tourSnapshot]
  );
  const cancellationRows = useMemo(
    () =>
      normalizeListStrings(
        Array.isArray(booking?.cancellationJson)
          ? booking.cancellationJson
          : Array.isArray(tourSnapshot?.cancellationPolicies)
            ? tourSnapshot.cancellationPolicies
            : tourSnapshot?.cancellation
      ),
    [booking, tourSnapshot]
  );
  const paxLabel = useMemo(() => buildPaxLabel(booking?.paxJson), [booking]);

  const status = String(booking?.status || "").toUpperCase();
  const isConfirmed = status === "CONFIRMED";

  const canSendPaymentLink =
    status === "ON_HOLD" || status === "WAITING_PAYMENT" || status === "PARTIALLY_PAID";

  const canMarkPaid = status === "WAITING_PAYMENT" || status === "PARTIALLY_PAID";
  const canEdit = enabled && !isConfirmed;

  // voucher allowed (keep permissive; backend enforces)
  const canGenerateVoucher = enabled && status && status !== "DRAFT";

  const markPaidMutation = useAdminUpdateBookingStatusMutation();
  const upsertTravelPlanMutation = useAdminUpsertTravelPlanMutation();
  const deleteTravelPlanMutation = useAdminDeleteTravelPlanMutation();

  const generateHdfcMutation = useAdminGenerateHdfcPaymentLinkMutation();

  // ✅ voucher mutation
  const generateVoucherMutation = useAdminGenerateVoucherMutation();

  /* ---------------------------
   * Payment modal (manual)
   * ------------------------- */
  // const [paymentOpen, setPaymentOpen] = useState(false);
  // const [payProvider] = useState<PaymentProvider>("MANUAL");
  // const [payKind] = useState<PaymentKind>("FULL");
  // const [payUrl, setPayUrl] = useState("");
  // const payUrlOk = isHttpsUrl(payUrl.trim());

  /* ---------------------------
   * HDFC generate modal
   * ------------------------- */
  const [genOpen, setGenOpen] = useState(false);
  const [genKind, setGenKind] = useState<Exclude<PaymentKind, "REFUND">>("FULL");
  const [genExpiresAt, setGenExpiresAt] = useState<string>(""); // YYYY-MM-DD
  const [genNote, setGenNote] = useState<string>("");

  const [generated, setGenerated] = useState<null | {
    paymentId: number;
    paymentLinkUrl: string;
    expiresAt?: string | null;
    amount?: string | number | null;
    currencyCode?: string | null;
    kind?: PaymentKind | null;
    provider?: PaymentProvider | null;
  }>(null);

  const generatedUrlOk = generated?.paymentLinkUrl ? isHttpsUrl(generated.paymentLinkUrl) : false;

  const savedLink = booking?.paymentLink ?? null;
  const hasSaved = !!savedLink?.url;
  const isFlightBooking = useMemo(() => detectFlightBooking(booking, items), [booking, items]);
  const flightTicketUrl = useMemo(() => resolveFlightTicketUrl(booking, items), [booking, items]);
  const flightDetail = useMemo(() => (payload as any)?.flight ?? null, [payload]);
  const flightSegments = useMemo(() => {
    const dbSegs = Array.isArray(flightDetail?.segments) ? flightDetail.segments : [];
    const mergedBooking = dbSegs.length ? { ...booking, Segments: dbSegs } : booking;
    return extractFlightSegments(mergedBooking, items);
  }, [booking, items, flightDetail]);
  const flightTrips = useMemo(() => {
    const map = new Map<number, FlightSegmentView[]>();
    for (const seg of flightSegments) {
      const key = seg.tripIndicator ?? 1;
      const prev = map.get(key) ?? [];
      prev.push(seg);
      map.set(key, prev);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [flightSegments]);
  const itineraryNode = useMemo(() => getFlightItineraryNode(payload), [payload]);
  const itineraryFare = useMemo(() => (itineraryNode as any)?.Fare ?? null, [itineraryNode]);
  const paidCurrencyCode = useMemo(
    () =>
      String(
        (booking as any)?.pricingSnapshot?.currency?.charge ||
          (booking as any)?.pricingSnapshot?.paidCurrencyCode ||
          (booking as any)?.pricingSnapshot?.chargeCurrencyCode ||
          (booking as any)?.pricingSnapshot?.currency?.display ||
          (booking as any)?.pricingSnapshot?.displayCurrencyCode ||
          (booking as any)?.currencyCode ||
          "INR"
      )
        .trim()
        .toUpperCase() || "INR",
    [booking]
  );
  const displayCurrencyCode = useMemo(
    () =>
      String(
        (booking as any)?.pricingSnapshot?.currency?.display ||
          (booking as any)?.pricingSnapshot?.displayCurrencyCode ||
          paidCurrencyCode
      )
        .trim()
        .toUpperCase() || paidCurrencyCode,
    [booking, paidCurrencyCode]
  );
  const bookingCurrencyCode = useMemo(
    () =>
      String(
        (booking as any)?.pricingSnapshot?.currency?.display ||
          (booking as any)?.pricingSnapshot?.currency?.charge ||
          (booking as any)?.pricingSnapshot?.displayCurrencyCode ||
        (booking as any)?.pricingSnapshot?.paidCurrencyCode ||
          (booking as any)?.currencyCode ||
          (booking as any)?.pricingSnapshot?.chargeCurrencyCode ||
          "INR"
      )
        .trim()
        .toUpperCase() || "INR",
    [booking]
  );
  const supplierCurrencyCode = useMemo(
    () =>
      String(
        (booking as any)?.pricingSnapshot?.supplierCurrencyCode ||
          (flightDetail as any)?.detail?.rawResponseJson?._tranzyMeta?.supplierCurrencyCode ||
          (itineraryFare as any)?.Currency ||
          "INR"
      )
        .trim()
        .toUpperCase() || "INR",
    [booking, flightDetail, itineraryFare]
  );
  const fareDisplay = useMemo(() => {
    const supplierBaseFare = safeNum((itineraryFare as any)?.BaseFare ?? booking?.subtotal) ?? 0;
    const supplierTaxFare = safeNum((itineraryFare as any)?.Tax ?? booking?.taxTotal) ?? 0;
    const supplierFare = safeNum((itineraryFare as any)?.OfferedFare ?? booking?.grandTotal) ?? 0;
    const supplierAncillary = Math.max(0, supplierFare - supplierBaseFare - supplierTaxFare);
    const paidTotal = safeNum(booking?.paidTotal ?? booking?.grandTotal) ?? 0;
    const storedDisplayAmount = safeNum((booking as any)?.pricingSnapshot?.displayAmount);

    const snapshotFx = safeNum((booking as any)?.pricingSnapshot?.currency?.fxRate);
    const sameCurrency = paidCurrencyCode === supplierCurrencyCode;
    const hasDisplayCurrency = displayCurrencyCode !== paidCurrencyCode;
    const snapshotPaidToDisplayFx = safeNum(
      (booking as any)?.pricingSnapshot?.currency?.paidToDisplayFxRate ??
        (booking as any)?.pricingSnapshot?.paidToDisplayFxRate
    );
    const snapshotDisplayToPaidFx = safeNum(
      (booking as any)?.pricingSnapshot?.currency?.displayFxRate ??
        (booking as any)?.pricingSnapshot?.displayFxRate
    );
    const paidToDisplayFx =
      !hasDisplayCurrency
        ? 1
        : snapshotPaidToDisplayFx && snapshotPaidToDisplayFx > 0
          ? snapshotPaidToDisplayFx
          : snapshotDisplayToPaidFx && snapshotDisplayToPaidFx > 0
            ? 1 / snapshotDisplayToPaidFx
            : storedDisplayAmount && paidTotal > 0
              ? storedDisplayAmount / paidTotal
              : null;
    const inferredFx =
      sameCurrency
        ? 1
        : snapshotFx && snapshotFx > 0
          ? snapshotFx
          : supplierFare > 0 && paidTotal > 0
            ? paidTotal / supplierFare
            : null;
    const toPaid = (amount: number) =>
      sameCurrency ? amount : inferredFx && inferredFx > 0 ? amount * inferredFx : null;
    const toSupplier = (amount: number) =>
      sameCurrency ? amount : inferredFx && inferredFx > 0 ? amount / inferredFx : null;
    const toDisplay = (amount: number) =>
      !hasDisplayCurrency ? amount : paidToDisplayFx && paidToDisplayFx > 0 ? amount * paidToDisplayFx : null;

    const paidBaseFare = toPaid(supplierBaseFare);
    const paidTaxFare = toPaid(supplierTaxFare);
    const paidFare = toPaid(supplierFare);
    const paidAncillary = toPaid(supplierAncillary);
    const supplierTotalFromPaid = toSupplier(paidTotal);
    const displayBaseFare = paidBaseFare !== null ? toDisplay(paidBaseFare) : null;
    const displayTaxFare = paidTaxFare !== null ? toDisplay(paidTaxFare) : null;
    const displayFare = paidFare !== null ? toDisplay(paidFare) : null;
    const displayAncillary = paidAncillary !== null ? toDisplay(paidAncillary) : null;
    const displayTotalFromPaid = toDisplay(paidTotal);

    return {
      sameCurrency,
      hasDisplayCurrency,
      supplierBaseFare,
      supplierTaxFare,
      supplierAncillary,
      supplierFare,
      paidTotal,
      paidBaseFare,
      paidTaxFare,
      paidAncillary,
      paidFare,
      supplierTotalFromPaid,
      displayBaseFare,
      displayTaxFare,
      displayAncillary,
      displayFare,
      displayTotalFromPaid,
    };
  }, [itineraryFare, booking, paidCurrencyCode, supplierCurrencyCode, displayCurrencyCode]);
  const passengerSsrRows = useMemo(() => extractPassengerSsr(payload), [payload]);
  const flightGstDetails = useMemo(() => extractFlightGstDetails(payload), [payload]);

  // ✅ voucher local state (show details OUTSIDE modal)
  const [voucher, setVoucher] = useState<AdminGenerateVoucherResult | null>(null);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherDialogForceReissue, setVoucherDialogForceReissue] = useState(false);
  const [voucherVendorRefNumber, setVoucherVendorRefNumber] = useState("");
  const [voucherDialogError, setVoucherDialogError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) return;
    const fromBooking = normalizeVoucherSnapshot((booking as any)?.voucher ?? null);
    if (fromBooking) {
      setVoucher(fromBooking);
      writeStoredVoucher(bookingId, fromBooking);
      return;
    }

    const fromStorage = readStoredVoucher(bookingId);
    if (fromStorage) setVoucher(fromStorage);
  }, [bookingId, booking]);

  const openVoucherDialog = (forceReissue: boolean) => {
    setVoucherDialogForceReissue(forceReissue);
    setVoucherVendorRefNumber(String(voucher?.vendorRefNumber ?? "").trim());
    setVoucherDialogError(null);
    setVoucherDialogOpen(true);
  };


  const onGenerateHdfcLink = async () => {
    if (!bookingId) return;

    const body: AdminGeneratePaymentLinkBody = {
      provider: "HDFC",
      kind: genKind,
      ...(genExpiresAt ? { expiresAt: genExpiresAt } : {}),
      ...(genNote ? { note: genNote } : {}),
    } as any;

    const res = (await generateHdfcMutation.mutateAsync({
      id: bookingId,
      data: body,
    })) as any as AdminGeneratePaymentLinkResult;

    setGenerated({
      paymentId: res.paymentId,
      paymentLinkUrl: res.paymentLinkUrl,
      expiresAt: (res as any)?.expiresAt ?? (body as any).expiresAt ?? null,
      currencyCode: bookingCurrencyCode,
      amount:
        genKind === "FULL"
          ? booking?.dueTotal ?? booking?.grandTotal
          : (((booking?.dueTotal ?? booking?.grandTotal) ?? 0) / 2),
      kind: genKind,
      provider: "HDFC",
    });
  };

  // ✅ NEW: voucher generate (shows outside, open new tab, share)
  const onGenerateVoucher = async () => {
    if (!bookingId) return;

    const ref = voucherVendorRefNumber.trim();
    if (!ref) {
      setVoucherDialogError("Vendor Ref is required.");
      return;
    }

    setVoucherDialogError(null);

    try {
      const body: AdminGenerateVoucherBody = {
        forceReissue: voucherDialogForceReissue,
        vendorRefNumber: ref,
      } as any;

      const res = (await generateVoucherMutation.mutateAsync({
        id: bookingId,
        data: body,
      })) as AdminGenerateVoucherResult;

      setVoucher(res);
      writeStoredVoucher(bookingId, res);
      setVoucherDialogOpen(false);
      setVoucherDialogError(null);
      setVoucherVendorRefNumber("");

      detailQ.refetch();
    } catch (error) {
      setVoucherDialogError(formatVoucherError(error));
    }
  };

  const onMarkPaid = async () => {
    if (!bookingId) return;
    await markPaidMutation.mutateAsync({
      id: bookingId,
      data: { status: "CONFIRMED" as any },
    });
    detailQ.refetch();
  };

  const onDownloadTicket = async () => {
    if (!booking) return;

    // Flight bookings: generate the SAME branded e-ticket PDF as the web app
    // (logo header, passenger/flight/ancillary tables, payment summary).
    const itinerary = getFlightItineraryNode({ booking });
    if (itinerary && (itinerary.Segments || itinerary.Passenger)) {
      try {
        const [{ generateFlightTicketPdfBlob }, { buildFlightTicketPayloadFromBooking }] =
          await Promise.all([
            import("./pdf/generateFlightTicketPdf"),
            import("./pdf/buildFlightTicketPayload"),
          ]);
        const payload = buildFlightTicketPayloadFromBooking(booking);
        const blob = await generateFlightTicketPdfBlob(payload);
        const ticketPnr = payload.booked?.pnr || booking?.bookingCode || "ticket";
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `flight-ticket-${ticketPnr}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      } catch {
        // fall through to the hosted PDF / printable fallback below
      }
    }

    // Prefer a real hosted e-ticket PDF if the supplier provided one.
    if (flightTicketUrl) {
      openExternal(flightTicketUrl);
      return;
    }

    // Otherwise render a printable e-ticket from the booking data so the user
    // can print or "Save as PDF" — instead of dumping raw JSON.
    const pnr =
      asText((flightDetail as any)?.detail?.supplierPnr) ||
      asText((booking as any)?.pnr) ||
      asText((booking as any)?.supplierPnr);

    printHtmlDocument(
      buildFlightTicketHtml({
        bookingCode: String(
          booking?.bookingCode || (booking?.id != null ? `#${booking.id}` : "")
        ),
        pnr,
        customerName,
        customerEmail,
        customerPhone,
        segments: flightSegments,
        travelers,
      })
    );
  };

  // const onSavePaymentLink = async () => {
  //   if (!bookingId) return;

  //   await setPaymentMutation.mutateAsync({
  //     id: bookingId,
  //     data: {
  //       provider: payProvider || "MANUAL",
  //       kind: payKind,
  //       paymentLink: payUrl.trim(),
  //       paymentId: null,
  //     },
  //   } as any);

  //   setPaymentOpen(false);
  //   setPayUrl("");
  //   detailQ.refetch();
  // };

  /* ---------------------------
   * Travel plan modal
   * ------------------------- */
  const [tpOpen, setTpOpen] = useState(false);
  const [tpError, setTpError] = useState<string | null>(null);

  const [tpStatus, setTpStatus] = useState<TravelPlanStatus>("NOT_SHARED");
  const [segments, setSegments] = useState<SegmentDraft[]>([]);

  const currentPlan: AdminTravelPlanResult | null = travelPlanQ.data ?? null;

  const openTravelPlan = () => {
    const p = currentPlan;
    setTpError(null);

    const segs = Array.isArray(p?.segmentsJson) ? p!.segmentsJson : [];
    const draftSegs = segs.map(segmentFromApi);

    setSegments(draftSegs);
    setTpStatus(computeTpStatus(draftSegs));
    setTpOpen(true);
  };

  const addSegment = () => {
    setSegments((prev) => [
      ...prev,
      {
        key: makeKey(),
        mode: "AIR" as TravelMode,
        date: "",
        from: "",
        to: "",
        details: "",
        departure: "",
        arrival: "",
      },
    ]);
  };

  const removeSegment = (key: string) => {
    setSegments((prev) => {
      const next = prev.filter((x) => x.key !== key);
      setTpStatus(computeTpStatus(next));
      return next;
    });
  };

  const updateSegment = (key: string, patch: Partial<SegmentDraft>) => {
    setSegments((prev) => {
      const next = prev.map((x) => (x.key === key ? { ...x, ...patch } : x));
      setTpStatus(computeTpStatus(next));
      return next;
    });
  };

  const onSaveTravelPlan = async () => {
    if (!bookingId) return;

    setTpError(null);

    const segErrs = validateSegments(segments);
    if (segErrs.length) {
      setTpError(segErrs[0]);
      return;
    }

    const segsApi = segments.map(segmentToApi);
    const computedStatus: TravelPlanStatus = segsApi.length > 0 ? "SHARED" : "NOT_SHARED";

    const body: AdminUpsertTravelPlanBody = {
      status: computedStatus,
      segmentsJson: segsApi,
    };

    await upsertTravelPlanMutation.mutateAsync({ bookingId, data: body });

    setTpOpen(false);
    travelPlanQ.refetch();
  };

  const onResetTravelPlan = async () => {
    if (!bookingId) return;

    setTpError(null);

    const body: AdminUpsertTravelPlanBody = {
      status: "NOT_SHARED",
      segmentsJson: [],
    };

    await upsertTravelPlanMutation.mutateAsync({ bookingId, data: body });

    setSegments([]);
    setTpStatus("NOT_SHARED");
    setTpOpen(false);
    travelPlanQ.refetch();
  };

  /* ============================================================================
     Voucher share content
  ============================================================================ */

  const voucherShareText = useMemo(() => {
    if (!voucher?.url) return "";
    const code = booking?.bookingCode || (booking?.id != null ? `#${booking.id}` : "");
    const name = customerName && customerName !== "—" ? customerName : "";
    const trip =
      booking?.travelStartDate || booking?.travelEndDate
        ? `Travel: ${formatDateShort(booking?.travelStartDate)} → ${formatDateShort(booking?.travelEndDate)}`
        : "";

    return [
      "Your travel voucher is ready.",
      code ? `Booking: ${code}` : "",
      name ? `Passenger: ${name}` : "",
      trip,
      "",
      "Voucher PDF:",
      voucher.url,
    ]
      .filter(Boolean)
      .join("\n");
  }, [voucher?.url, booking?.bookingCode, booking?.id, booking?.travelStartDate, booking?.travelEndDate, customerName]);

  const emailSubject = useMemo(() => {
    const code = booking?.bookingCode || (booking?.id != null ? `#${booking.id}` : "Booking");
    return `Voucher - ${code}`;
  }, [booking?.bookingCode, booking?.id]);

  /* ============================================================================
     UI
  ============================================================================ */

  return (
    <AdminLayout>
      {/* Top bar */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Link to={listPath} className="text-gray-500 hover:text-gray-700">
            Bookings
          </Link>
          <span className="text-gray-300">/</span>
          <span className="font-medium text-gray-900">
            {booking?.bookingCode || (booking ? `#${booking.id}` : "Details")}
          </span>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {isFlightBooking && (
            <button
              onClick={onDownloadTicket}
              className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-50"
              type="button"
              title={
                flightTicketUrl
                  ? "Open supplier ticket PDF in a new tab"
                  : "Download booking ticket payload as JSON"
              }
            >
              Download ticket
            </button>
          )}

          {!isFlightBooking && (
            <>
              {status !== "ON_HOLD" && status !== "WAITING_PAYMENT" && (
                <>
              <button
                disabled={!canGenerateVoucher || generateVoucherMutation.isPending}
                onClick={() => openVoucherDialog(false)}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium border",
                  !canGenerateVoucher
                    ? "border-gray-200 bg-gray-50 text-gray-400"
                    : generateVoucherMutation.isPending
                    ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                    : "border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50"
                )}
                type="button"
                title="Generate voucher PDF and open in a new tab"
              >
                {generateVoucherMutation.isPending ? "Generating voucher…" : "Generate voucher"}
              </button>

              <button
                disabled={!canGenerateVoucher || generateVoucherMutation.isPending}
                onClick={() => openVoucherDialog(true)}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium",
                  !canGenerateVoucher
                    ? "bg-gray-200 text-gray-500"
                    : generateVoucherMutation.isPending
                    ? "bg-indigo-200 text-indigo-800"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                )}
                type="button"
                title="Force reissue (new version) and open in a new tab"
              >
                Reissue voucher
              </button>
                </>
              )}

              <button
                disabled={!enabled || travelPlanQ.isLoading || !canEdit}
                onClick={openTravelPlan}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium border",
                  canEdit
                    ? "border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-50 text-gray-400"
                )}
                type="button"
              >
                Travel plan
              </button>
            </>
          )}

          {enabled && !isConfirmed && (
            <>
              {canSendPaymentLink && (
                <button
                  onClick={() => setGenOpen(true)}
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
                  type="button"
                >
                  Generate HDFC link
                </button>
              )}

              {canMarkPaid && (
                <button
                  onClick={onMarkPaid}
                  disabled={markPaidMutation.isPending}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium",
                    markPaidMutation.isPending
                      ? "bg-emerald-200 text-emerald-800"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  )}
                  type="button"
                >
                  {markPaidMutation.isPending ? "Marking…" : "Mark as Paid"}
                </button>
              )}
            </>
          )}

        </div>
      </div>

      {!isFlightBooking && voucher && (
        <div className="mb-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-indigo-900">
                {voucher.url ? "Voucher generated" : "Voucher generated and emailed"}
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-indigo-900">
                <div>
                  <div className="text-indigo-700">Voucher #</div>
                  <div className="font-medium">{voucher.voucherNumber || "—"}</div>
                </div>
                <div>
                  <div className="text-indigo-700">Version</div>
                  <div className="font-medium">{String((voucher as any).version ?? "—")}</div>
                </div>
                <div>
                  <div className="text-indigo-700">Vendor Ref #</div>
                  <div className="font-medium">{voucher.vendorRefNumber ?? "—"}</div>
                </div>
                {voucher.url ? (
                  <div className="col-span-2">
                    <div className="text-indigo-700">PDF URL</div>
                    <div className="mt-0.5 truncate font-medium">{voucher.url}</div>
                  </div>
                ) : (
                  <div className="col-span-2">
                    <div className="text-indigo-700">Delivery</div>
                    <div className="mt-0.5 font-medium">
                      PDF attached in the customer email.
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {voucher.url ? (
                  <>
                    <button
                      type="button"
                      onClick={() => openExternal(voucher.url!)}
                      className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
                    >
                      Open in new tab →
                    </button>

                    <a
                      href={whatsappHref(voucherShareText)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-green-200 bg-white px-3 py-2 text-sm font-medium text-green-800 hover:bg-green-50"
                    >
                      Share on WhatsApp
                    </a>

                    <a
                      href={mailtoHref({
                        to: customerEmail && customerEmail !== "—" ? String(customerEmail) : "",
                        subject: emailSubject,
                        body: voucherShareText,
                      })}
                      className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-50"
                    >
                      Share by Email
                    </a>

                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(voucher.url!);
                        } catch {
                          // ignore
                        }
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Copy link
                    </button>
                  </>
                ) : null}
              </div>

              <div className="mt-2 text-xs text-indigo-700">
                {voucher.url
                  ? "Tip: WhatsApp/Email sharing here opens the client apps with the message prefilled."
                  : "The voucher PDF is attached to the customer email."}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setVoucher(null)}
              className="self-start rounded-lg border border-indigo-200 bg-white px-2 py-2 text-indigo-700 hover:bg-indigo-100"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <Modal
        open={!isFlightBooking && voucherDialogOpen}
        onClose={() => {
          if (generateVoucherMutation.isPending) return;
          setVoucherDialogOpen(false);
          setVoucherDialogError(null);
        }}
        title={voucherDialogForceReissue ? "Reissue voucher" : "Generate voucher"}
        subtitle="Enter Vendor Ref and continue."
        widthClass="max-w-lg"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setVoucherDialogOpen(false);
                setVoucherDialogError(null);
              }}
              disabled={generateVoucherMutation.isPending}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onGenerateVoucher}
              disabled={generateVoucherMutation.isPending}
              className={cn(
                "rounded-lg px-3 py-2 text-sm font-medium text-white",
                generateVoucherMutation.isPending ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700"
              )}
            >
              {generateVoucherMutation.isPending
                ? voucherDialogForceReissue
                  ? "Reissuing..."
                  : "Generating..."
                : voucherDialogForceReissue
                ? "Reissue voucher"
                : "Generate voucher"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Vendor Ref</label>
            <input
              autoFocus
              value={voucherVendorRefNumber}
              onChange={(e) => {
                setVoucherVendorRefNumber(e.target.value);
                if (voucherDialogError) setVoucherDialogError(null);
              }}
              placeholder="Enter Vendor Ref"
              className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20"
              disabled={generateVoucherMutation.isPending}
            />
          </div>

          {voucherDialogError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {voucherDialogError}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Loading / error */}
      {detailQ.isError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load booking.
          <button
            onClick={() => detailQ.refetch()}
            className="ml-3 rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {payload && booking && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left */}
          <div className="lg:col-span-2 space-y-4">
            {/* Customer summary */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="text-sm font-semibold text-gray-900">Customer</div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <div className="text-xs text-gray-500">Name</div>
                  <div className="mt-0.5 text-sm font-medium text-gray-900">{customerName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="mt-0.5 text-sm text-gray-900">{customerEmail}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="mt-0.5 text-sm text-gray-900">{customerPhone}</div>
                </div>
              </div>
            </div>

            {!isFlightBooking ? (
              <>
                {/* Hero */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <div className="relative">
                    {headerImageUrl ? (
                      <img src={headerImageUrl} alt={title} className="h-56 w-full object-cover" />
                    ) : (
                      <div className="h-56 w-full bg-gray-100" />
                    )}

                    <div className="absolute left-4 top-4 flex items-center gap-2">
                      <StatusPill status={booking.status} />
                      {cityLabel && (
                        <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs text-gray-700 backdrop-blur">
                          {cityLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="text-lg font-semibold text-gray-900">{title}</div>
                        <div className="mt-1 text-sm text-gray-600">
                          Travel:{" "}
                          <span className="font-medium text-gray-800">
                            {formatDateShort(booking.travelStartDate)}
                          </span>{" "}
                          →{" "}
                          <span className="font-medium text-gray-800">
                            {formatDateShort(booking.travelEndDate)}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          Pax: <span className="font-medium text-gray-800">{paxLabel}</span>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-col items-end gap-1 sm:mt-0">
                        <div className="text-xs text-gray-500">Booking</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {booking.bookingCode || `#${booking.id}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          Updated {formatDateShort(booking.updatedAt || booking.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="text-xs text-gray-500">Subtotal</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                          {formatMoney(booking.subtotal, bookingCurrencyCode)}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="text-xs text-gray-500">Tax</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                          {formatMoney(booking.taxTotal, bookingCurrencyCode)}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="text-xs text-gray-500">Paid</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                          {formatMoney(booking.paidTotal, bookingCurrencyCode)}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="text-xs text-gray-500">Grand total</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                          {formatMoney(booking.grandTotal, bookingCurrencyCode)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tour brief */}
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">Tour brief</div>
                    <div className="text-xs text-gray-500">
                      {itineraryDays.length ? `${itineraryDays.length} days` : "—"}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Day-wise itinerary
                    </div>
                    {itineraryDays.length === 0 ? (
                      <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                        No itinerary available for this tour.
                      </div>
                    ) : (
                      <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
                        {itineraryDays.map((d: any, idx: number) => (
                          <div
                            key={`${d.dayNumber}-${d.title || ""}-${idx}`}
                            className={cn(
                              "grid gap-3 px-4 py-4 sm:grid-cols-[96px_minmax(0,1fr)]",
                              idx !== itineraryDays.length - 1 && "border-b border-gray-200"
                            )}
                          >
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Day {Number(d.dayNumber ?? idx + 1)}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900">
                                {String(d.title ?? "").trim() || "Scheduled plan"}
                              </div>
                              <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                                {String(d.description ?? "").trim() || "—"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Hotels
                      </div>
                      {hotelRows.length === 0 ? (
                        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                          No hotel details available.
                        </div>
                      ) : (
                        <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
                          {hotelRows.map((hotel: any, idx: number) => {
                            const meta = [
                              hotel.hotelCategory,
                              hotel.roomType,
                              hotel.mealPlan,
                              [hotel.city, hotel.country].filter(Boolean).join(", "),
                            ].filter(Boolean);
                            const timing = [hotel.date ? formatDateShort(hotel.date) : null, hotel.startTime, hotel.endTime]
                              .filter(Boolean)
                              .join(" • ");

                            return (
                              <div
                                key={hotel.key}
                                className={cn("px-4 py-4", idx !== hotelRows.length - 1 && "border-b border-gray-200")}
                              >
                                <div className="text-sm font-semibold text-gray-900">{hotel.hotelName}</div>
                                {meta.length ? (
                                  <div className="mt-1 text-xs text-gray-500">{meta.join(" • ")}</div>
                                ) : null}
                                {timing ? <div className="mt-2 text-sm text-gray-700">{timing}</div> : null}
                                {hotel.confirmationNumber ? (
                                  <div className="mt-1 text-sm text-gray-700">
                                    Confirmation: <span className="font-medium text-gray-900">{hotel.confirmationNumber}</span>
                                  </div>
                                ) : null}
                                {hotel.notes ? <div className="mt-1 text-sm text-gray-700">{hotel.notes}</div> : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Inclusions
                        </div>
                        {inclusionRows.length === 0 ? (
                          <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                            No inclusions available.
                          </div>
                        ) : (
                          <div className="mt-2 rounded-xl border border-gray-200 px-4 py-3">
                            <ul className="space-y-2 text-sm text-gray-700">
                              {inclusionRows.map((item, idx) => (
                                <li key={`${item}-${idx}`} className="flex gap-2">
                                  <span className="mt-[2px] text-emerald-600">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Exclusions
                        </div>
                        {exclusionRows.length === 0 ? (
                          <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                            No exclusions available.
                          </div>
                        ) : (
                          <div className="mt-2 rounded-xl border border-gray-200 px-4 py-3">
                            <ul className="space-y-2 text-sm text-gray-700">
                              {exclusionRows.map((item, idx) => (
                                <li key={`${item}-${idx}`} className="flex gap-2">
                                  <span className="mt-[2px] text-rose-500">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Cancellation policy
                        </div>
                        {cancellationRows.length === 0 ? (
                          <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                            No cancellation policy available.
                          </div>
                        ) : (
                          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
                            <ul className="space-y-2 text-sm text-gray-700">
                              {cancellationRows.map((item, idx) => (
                                <li key={`${item}-${idx}`} className="flex gap-2">
                                  <span className="mt-[2px] text-amber-600">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-semibold text-gray-900">Flight Booking</div>
                    <StatusPill status={booking.status} />
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    Booking:{" "}
                    <span className="font-medium text-gray-900">
                      {booking.bookingCode || `#${booking.id}`}
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">Flight Itinerary</div>
                    <div className="text-xs text-gray-500">
                      {flightSegments.length ? `${flightSegments.length} segments` : "—"}
                    </div>
                  </div>

                  {flightTrips.length === 0 ? (
                    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                      Flight segments are not available in this booking snapshot.
                    </div>
                  ) : (
                    <div className="mt-3 space-y-3">
                      {flightTrips.map(([tripNo, segs], tripIdx) => (
                        <div key={`trip-${tripNo}-${tripIdx}`} className="rounded-xl border border-gray-200">
                          <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">
                            {tripIdx === 0 && flightTrips.length > 1
                              ? "Outbound"
                              : tripIdx === 1 && flightTrips.length > 1
                              ? "Inbound"
                              : `Trip ${tripNo || tripIdx + 1}`}
                          </div>
                          <div className="divide-y divide-gray-100">
                            {segs.map((seg, i) => {
                              const dep = formatDateTimeParts(seg.departureAt);
                              const arr = formatDateTimeParts(seg.arrivalAt);
                              return (
                                <div key={`${tripNo}-${i}-${seg.fromCode}-${seg.toCode}`} className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-3">
                                  <div>
                                    <div className="text-lg font-semibold text-gray-900">{seg.fromCode || "—"}</div>
                                    <div className="text-sm text-gray-700">{dep.time}</div>
                                    <div className="text-xs text-gray-500">{dep.date}</div>
                                    {seg.fromCity ? <div className="text-xs text-gray-500">{seg.fromCity}</div> : null}
                                  </div>
                                  <div className="sm:text-center">
                                    <div className="flex items-center gap-2 sm:justify-center">
                                      <img
                                        src={airlineLogoUrl(seg.airlineCode)}
                                        alt={seg.airlineCode || seg.airline || "Airline"}
                                        className="h-6 w-6 rounded object-contain"
                                        loading="lazy"
                                        onError={(e) => {
                                          const img = e.currentTarget;
                                          if (!img.src.includes("nologo.gif")) {
                                            img.src = airlineLogoUrl("nologo");
                                          }
                                        }}
                                      />
                                      <div className="text-sm font-semibold text-gray-900">
                                        {[seg.airline, seg.flightNumber].filter(Boolean).join(" ") || "Flight"}
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {[seg.duration ? `${seg.duration} min` : "", seg.cabin].filter(Boolean).join(" • ") || "—"}
                                    </div>
                                  </div>
                                  <div className="sm:text-right">
                                    <div className="text-lg font-semibold text-gray-900">{seg.toCode || "—"}</div>
                                    <div className="text-sm text-gray-700">{arr.time}</div>
                                    <div className="text-xs text-gray-500">{arr.date}</div>
                                    {seg.toCity ? <div className="text-xs text-gray-500">{seg.toCity}</div> : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Travelers */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Travelers</div>
                <div className="text-xs text-gray-500">
                  {travelers.length ? `${travelers.length} total` : "—"}
                </div>
              </div>

              <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600">
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Name</th>
                      <th className="px-3 py-2 text-left font-medium">Email / Phone</th>
                      <th className="px-3 py-2 text-left font-medium">Passport</th>
                    </tr>
                  </thead>
                  <tbody>
                    {travelers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                          No travelers found.
                        </td>
                      </tr>
                    ) : (
                      travelers.map((t: any) => (
                        <tr key={String(t.id)} className="border-b border-gray-100">
                          <td className="px-3 py-3 text-gray-700">{String(t.travelerType || "—")}</td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-gray-900">
                              {`${t.firstName || ""} ${t.lastName || ""}`.trim() || "—"}
                            </div>
                            <div className="text-xs text-gray-500">DOB: {formatDateShort(t.dob)}</div>
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            <div>{t.email || "—"}</div>
                            <div className="text-xs text-gray-500">{t.phone || "—"}</div>
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            <div>{t.passportNumber || "—"}</div>
                            <div className="text-xs text-gray-500">
                              Exp: {formatDateShort(t.passportExpiryDate)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {isConfirmed && (
                <div className="mt-3 text-xs text-gray-500">Travelers are view-only for confirmed bookings.</div>
              )}
            </div>
          </div>

          {/* Right */}
          <div className="space-y-4">
            {/* Payment */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Payment</div>
              </div>

              {hasSaved ? (
                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-gray-500">Saved link</div>
                      <div className="mt-1 truncate text-sm font-medium text-gray-900">
                        {savedLink.url}
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                        <div>
                          <div className="text-gray-500">Provider</div>
                          <div className="font-medium">{savedLink.provider || "—"}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Kind</div>
                          <div className="font-medium">{savedLink.kind || "—"}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Expires</div>
                          <div className="font-medium">
                            {savedLink.expiresAt ? formatDateShort(savedLink.expiresAt) : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Updated</div>
                          <div className="font-medium">
                            {savedLink.updatedAt ? formatDateShort(savedLink.updatedAt) : "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="rounded-lg border border-gray-200 bg-white px-2 py-2 text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        if (savedLink.url) openExternal(savedLink.url);
                      }}
                      title="Open payment link"
                    >
                      ↗
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 text-sm text-gray-600">No payment link saved yet.</div>
              )}
            </div>

            {isFlightBooking && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">Flight Fare</div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {formatMoney(
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayBaseFare !== null
                            ? fareDisplay.displayBaseFare
                            : fareDisplay.paidBaseFare ?? fareDisplay.supplierBaseFare,
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayBaseFare !== null
                            ? displayCurrencyCode
                            : fareDisplay.paidBaseFare === null
                              ? supplierCurrencyCode
                              : paidCurrencyCode
                        )}
                      </div>
                      {fareDisplay.hasDisplayCurrency || !fareDisplay.sameCurrency ? (
                        <div className="text-xs text-gray-500">
                          {supplierCurrencyCode} eqv: {formatMoney(fareDisplay.supplierBaseFare, supplierCurrencyCode)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Tax</span>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {formatMoney(
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayTaxFare !== null
                            ? fareDisplay.displayTaxFare
                            : fareDisplay.paidTaxFare ?? fareDisplay.supplierTaxFare,
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayTaxFare !== null
                            ? displayCurrencyCode
                            : fareDisplay.paidTaxFare === null
                              ? supplierCurrencyCode
                              : paidCurrencyCode
                        )}
                      </div>
                      {fareDisplay.hasDisplayCurrency || !fareDisplay.sameCurrency ? (
                        <div className="text-xs text-gray-500">
                          {supplierCurrencyCode} eqv: {formatMoney(fareDisplay.supplierTaxFare, supplierCurrencyCode)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Ancillary</span>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {formatMoney(
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayAncillary !== null
                            ? fareDisplay.displayAncillary
                            : fareDisplay.paidAncillary ?? fareDisplay.supplierAncillary,
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayAncillary !== null
                            ? displayCurrencyCode
                            : fareDisplay.paidAncillary === null
                              ? supplierCurrencyCode
                              : paidCurrencyCode
                        )}
                      </div>
                      {fareDisplay.hasDisplayCurrency || !fareDisplay.sameCurrency ? (
                        <div className="text-xs text-gray-500">
                          {supplierCurrencyCode} eqv: {formatMoney(fareDisplay.supplierAncillary, supplierCurrencyCode)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Supplier Fare</span>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {formatMoney(
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayFare !== null
                            ? fareDisplay.displayFare
                            : fareDisplay.paidFare ?? fareDisplay.supplierFare,
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayFare !== null
                            ? displayCurrencyCode
                            : fareDisplay.paidFare === null
                              ? supplierCurrencyCode
                              : paidCurrencyCode
                        )}
                      </div>
                      {fareDisplay.hasDisplayCurrency || !fareDisplay.sameCurrency ? (
                        <div className="text-xs text-gray-500">
                          {supplierCurrencyCode} eqv: {formatMoney(fareDisplay.supplierFare, supplierCurrencyCode)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                    <span className="font-medium text-gray-800">Total Paid</span>
                    <div className="text-right">
                      <div className="text-base font-semibold text-gray-900">
                        {formatMoney(
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayTotalFromPaid !== null
                            ? fareDisplay.displayTotalFromPaid
                            : fareDisplay.paidTotal,
                          fareDisplay.hasDisplayCurrency && fareDisplay.displayTotalFromPaid !== null
                            ? displayCurrencyCode
                            : paidCurrencyCode
                        )}
                      </div>
                      {(fareDisplay.hasDisplayCurrency || !fareDisplay.sameCurrency) &&
                      fareDisplay.supplierTotalFromPaid !== null ? (
                        <div className="text-xs text-gray-500">
                          {supplierCurrencyCode} eqv: {formatMoney(fareDisplay.supplierTotalFromPaid, supplierCurrencyCode)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFlightBooking && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Ancillary Services (SSR)</div>
                  <div className="text-xs text-gray-500">{passengerSsrRows.length ? `${passengerSsrRows.length} pax` : "—"}</div>
                </div>
                {passengerSsrRows.length === 0 ? (
                  <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                    No SSR details in supplier response.
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {passengerSsrRows.map((row: any, idx: number) => (
                      <div key={`${row.name}-${idx}`} className="rounded-xl border border-gray-200 p-3">
                        <div className="text-sm font-medium text-gray-900">{row.name}</div>
                        <div className="mt-1 text-xs text-gray-600">Baggage: {row.baggage.join(", ") || "None"}</div>
                        <div className="text-xs text-gray-600">Meal: {row.meals.join(", ") || "None"}</div>
                        <div className="text-xs text-gray-600">Seat: {row.seats.join(", ") || "None"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isFlightBooking && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">Supplier Booking</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
                  <div>
                    <div className="text-gray-500">PNR</div>
                    <div className="font-medium">{flightDetail?.detail?.supplierPnr || "—"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Supplier Booking ID</div>
                    <div className="font-medium">{flightDetail?.detail?.supplierBookingId ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Paid Currency</div>
                    <div className="font-medium">{paidCurrencyCode}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Supplier Currency</div>
                    <div className="font-medium">{supplierCurrencyCode}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500">Trace ID</div>
                    <div className="font-medium break-all">{flightDetail?.detail?.supplierTraceId || "—"}</div>
                  </div>
                </div>
              </div>
            )}

            {isFlightBooking && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">GST Details</div>
                  <div
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                      flightGstDetails.hasGst
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-gray-200 bg-gray-50 text-gray-600"
                    )}
                  >
                    {flightGstDetails.hasGst ? "GST Applied" : "GST Not Applied"}
                  </div>
                </div>
                {!flightGstDetails.hasGst ? (
                  <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                    No GST details available for this booking.
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-700 sm:grid-cols-2">
                    <div>
                      <div className="text-gray-500">GST/UIN Number</div>
                      <div className="font-medium">{flightGstDetails.number || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">GST Company Name</div>
                      <div className="font-medium">{flightGstDetails.companyName || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">GST Company Email</div>
                      <div className="font-medium">{flightGstDetails.companyEmail || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">GST Company Contact No</div>
                      <div className="font-medium">{flightGstDetails.companyContactNumber || "—"}</div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="text-gray-500">GST Company Address</div>
                      <div className="font-medium break-words">{flightGstDetails.companyAddress || "—"}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isFlightBooking && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Travel plan</div>

                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                      currentPlan?.status === "SHARED"
                        ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                        : "border-gray-200 bg-gray-50 text-gray-700"
                    )}
                  >
                    {prettyEnumLabel(currentPlan?.status || "NOT_SHARED")}
                  </span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">Segments</div>
                    <div className="text-xs text-gray-500">
                      {Array.isArray(currentPlan?.segmentsJson) && currentPlan.segmentsJson.length
                        ? `${currentPlan.segmentsJson.length} segments`
                        : "—"}
                    </div>
                  </div>

                  {Array.isArray(currentPlan?.segmentsJson) && currentPlan.segmentsJson.length ? (
                    <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
                      <div className="max-h-[260px] overflow-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-xs text-gray-600">
                            <tr className="border-b border-gray-200">
                              <th className="px-3 py-2 text-left font-medium">Mode</th>
                              <th className="px-3 py-2 text-left font-medium">Date</th>
                              <th className="px-3 py-2 text-left font-medium">From → To</th>
                              <th className="px-3 py-2 text-left font-medium">Details</th>
                              <th className="px-3 py-2 text-left font-medium">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentPlan.segmentsJson.map((seg, idx) => {
                              const dep = (seg.departure || "").trim();
                              const arr = (seg.arrival || "").trim();
                              const timeLabel = dep || arr ? `${dep || "—"} → ${arr || "—"}` : "—";

                              return (
                                <tr key={`${idx}-${seg.date}-${seg.mode}`} className="border-b border-gray-100">
                                  <td className="px-3 py-2 text-gray-800">{seg.mode}</td>
                                  <td className="px-3 py-2 text-gray-800">{formatDateShort(seg.date)}</td>
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900">
                                      {(seg.from || "—").trim()} → {(seg.to || "—").trim()}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">{(seg.details || "—").trim()}</td>
                                  <td className="px-3 py-2 text-gray-700">{timeLabel}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
                      No segments added yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {isConfirmed && (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                This booking is confirmed and locked.
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================
          TRAVEL PLAN MODAL
      ========================== */}
      <Modal
        open={!isFlightBooking && tpOpen}
        title="Travel plan"
        subtitle="Segments only (auto status)"
        onClose={() => {
          if (upsertTravelPlanMutation.isPending || deleteTravelPlanMutation.isPending) return;
          setTpOpen(false);
        }}
        widthClass="max-w-5xl"
        footer={
          <>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {isConfirmed ? "Locked for confirmed bookings." : "Save to apply changes."}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setTpOpen(false)}
                disabled={upsertTravelPlanMutation.isPending || deleteTravelPlanMutation.isPending}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
              >
                Close
              </button>

              <button
                type="button"
                onClick={onResetTravelPlan}
                disabled={!canEdit || upsertTravelPlanMutation.isPending || deleteTravelPlanMutation.isPending}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium border",
                  !canEdit
                    ? "border-gray-200 bg-gray-50 text-gray-400"
                    : "border-red-200 bg-white text-red-700 hover:bg-red-50"
                )}
              >
                {deleteTravelPlanMutation.isPending ? "Resetting…" : "Reset"}
              </button>

              <button
                type="button"
                onClick={onSaveTravelPlan}
                disabled={!canEdit || upsertTravelPlanMutation.isPending}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium",
                  !canEdit || upsertTravelPlanMutation.isPending
                    ? "bg-gray-200 text-gray-500"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                )}
              >
                {upsertTravelPlanMutation.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        }
      >
        <div className="space-y-4">
          {tpError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {tpError}
            </div>
          )}

          {!canEdit && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Travel plan is locked for confirmed bookings.
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">Status</div>
              <div className="text-sm font-medium text-gray-900">{prettyEnumLabel(tpStatus)}</div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">Segments</div>
              </div>

              <button
                type="button"
                onClick={addSegment}
                disabled={!canEdit}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium",
                  canEdit ? "bg-gray-900 text-white hover:bg-gray-800" : "bg-gray-200 text-gray-500"
                )}
              >
                Add segment
              </button>
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
              <div className="overflow-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600">
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-medium">Mode</th>
                      <th className="px-3 py-2 text-left font-medium">Details</th>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">From</th>
                      <th className="px-3 py-2 text-left font-medium">To</th>
                      <th className="px-3 py-2 text-left font-medium">Dep</th>
                      <th className="px-3 py-2 text-left font-medium">Arr</th>
                      <th className="px-3 py-2 text-right font-medium">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {segments.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-10 text-center text-gray-500">
                          No segments yet. Click <span className="font-medium">Add segment</span>.
                        </td>
                      </tr>
                    ) : (
                      segments.map((s) => (
                        <tr key={s.key} className="border-b border-gray-100">
                          <td className="px-3 py-2 align-top">
                            <select
                              value={s.mode}
                              onChange={(e) => updateSegment(s.key, { mode: e.target.value as TravelMode })}
                              disabled={!canEdit}
                              className="h-9 w-28 rounded-lg border border-gray-300 bg-white px-2 text-sm outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
                            >
                              {TRAVEL_MODE_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="px-3 py-2 align-top">
                            <input
                              value={s.details}
                              onChange={(e) => updateSegment(s.key, { details: e.target.value })}
                              disabled={!canEdit}
                              className="h-9 w-56 rounded-lg border border-gray-300 bg-white px-2 text-sm outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
                              placeholder="Flight no / Train / Bus"
                            />
                          </td>

                          <td className="px-3 py-2 align-top">
                            <input
                              type="date"
                              value={s.date}
                              onChange={(e) => updateSegment(s.key, { date: e.target.value })}
                              disabled={!canEdit}
                              className="h-9 w-36 rounded-lg border border-gray-300 bg-white px-2 text-sm outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
                            />
                          </td>

                          <td className="px-3 py-2 align-top">
                            <input
                              value={s.from}
                              onChange={(e) => updateSegment(s.key, { from: e.target.value })}
                              disabled={!canEdit}
                              className="h-9 w-36 rounded-lg border border-gray-300 bg-white px-2 text-sm outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
                              placeholder="DEL / NYC"
                            />
                          </td>

                          <td className="px-3 py-2 align-top">
                            <input
                              value={s.to}
                              onChange={(e) => updateSegment(s.key, { to: e.target.value })}
                              disabled={!canEdit}
                              className="h-9 w-36 rounded-lg border border-gray-300 bg-white px-2 text-sm outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:bg-gray-50"
                              placeholder="DXB / LAX"
                            />
                          </td>

                          <td className="px-3 py-2 align-top">
                            <TimeHmCell
                              value={s.departure || null}
                              disabled={!canEdit}
                              onChange={(departure) => updateSegment(s.key, { departure: departure ?? "" })}
                            />
                          </td>

                          <td className="px-3 py-2 align-top">
                            <TimeHmCell
                              value={s.arrival || null}
                              disabled={!canEdit}
                              onChange={(arrival) => updateSegment(s.key, { arrival: arrival ?? "" })}
                            />
                          </td>

                          <td className="px-3 py-2 text-right align-top">
                            <button
                              type="button"
                              onClick={() => removeSegment(s.key)}
                              disabled={!canEdit}
                              className={cn(
                                "rounded-lg border px-2 py-1 text-xs font-medium",
                                canEdit
                                  ? "border-red-200 bg-white text-red-700 hover:bg-red-50"
                                  : "border-gray-200 bg-gray-50 text-gray-400"
                              )}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* =========================
          GENERATE HDFC MODAL
      ========================== */}
      <Modal
        open={genOpen}
        title="Generate HDFC payment link"
        subtitle="Generate → review → open"
        onClose={() => {
          if (generateHdfcMutation.isPending) return;
          setGenOpen(false);
        }}
        widthClass="max-w-xl"
        footer={
          <>
            <div className="text-xs text-gray-500">
              {generated
                ? "Link generated, attached to the booking, and emailed to the customer."
                : "Choose kind + optional expiry, then Generate."}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setGenOpen(false)}
                disabled={generateHdfcMutation.isPending}
                className={cn(
                  "rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm",
                  generateHdfcMutation.isPending
                    ? "text-gray-400"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                Close
              </button>

              {!generated ? (
                <button
                  type="button"
                  onClick={onGenerateHdfcLink}
                  disabled={!canEdit || generateHdfcMutation.isPending}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium",
                    !canEdit || generateHdfcMutation.isPending
                      ? "bg-indigo-200 text-indigo-800"
                      : "bg-indigo-600 text-white hover:bg-indigo-700"
                  )}
                >
                  {generateHdfcMutation.isPending ? "Generating…" : "Generate"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setGenOpen(false)}
                  disabled={!generatedUrlOk}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium",
                    !generatedUrlOk
                      ? "bg-emerald-200 text-emerald-800"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  )}
                >
                  Done
                </button>
              )}
            </div>
          </>
        }
      >
        <div className="space-y-3">
          {generateHdfcMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formatGeneratePaymentLinkError(generateHdfcMutation.error)}
            </div>
          )}

          {!generated ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs text-gray-500">Kind</div>
                  <select
                    value={genKind}
                    onChange={(e) =>
                      setGenKind(e.target.value as Exclude<PaymentKind, "REFUND">)
                    }
                  >
                    <option value="FULL">Full Payment</option>
                    <option value="PARTIAL">Half Payment</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1 text-xs text-gray-500">Expires at (optional)</div>
                  <input
                    type="date"
                    value={genExpiresAt}
                    onChange={(e) => setGenExpiresAt(e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs text-gray-500">Note (optional)</div>
                <input
                  value={genNote}
                  onChange={(e) => setGenNote(e.target.value)}
                  placeholder="Shown internally / audit notes"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20"
                />
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                Amount preview:{" "}
                <span className="font-semibold">
                  {formatMoney(
                    genKind === "FULL"
                      ? booking?.dueTotal ?? booking?.grandTotal
                      : (((booking?.dueTotal ?? booking?.grandTotal) ?? 0) / 2),
                    bookingCurrencyCode
                  )}
                </span>
                {" • "}
                Currency: <span className="font-semibold">{bookingCurrencyCode}</span>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                <div className="text-xs font-semibold text-emerald-900">Generated link</div>
                <div className="mt-1 truncate text-xs text-emerald-800">{generated.paymentLinkUrl}</div>

                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-emerald-900">
                  <div>
                    <div className="text-emerald-700">Provider</div>
                    <div className="font-medium">{generated.provider || "HDFC"}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700">Kind</div>
                    <div className="font-medium">{generated.kind || genKind}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700">Amount</div>
                    <div className="font-medium">{formatMoney(generated.amount, generated.currencyCode)}</div>
                  </div>
                  <div>
                    <div className="text-emerald-700">Expires</div>
                    <div className="font-medium">
                      {generated.expiresAt ? formatDateShort(generated.expiresAt) : "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => openExternal(generated.paymentLinkUrl)}
                    className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    Open →
                  </button>

                  <button
                    type="button"
                    onClick={() => setGenerated(null)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                This link is already attached to the booking and sent to the customer by email.
              </div>
            </div>
          )}
        </div>
      </Modal>


    </AdminLayout>
  );
}
